/**
 * The console's small, reused pieces: identifiers, timestamps, paging, and the
 * confirmation that has to state a price before it spends money.
 *
 * English-only and no `t()`. See the note at the top of `Console.tsx`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { isError, type ApiError } from './api';

// ---- Identifiers ----

/**
 * An identifier, in monospace, whole.
 *
 * Never elided. Half of what this console is for is copying a `userId` or an
 * `itemId` into an AWS CLI call, and an id shown as `01J8…F2` is an id that has to
 * be fetched again from somewhere else. Long ones wrap rather than truncate.
 */
export function Id({ value, className }: { value: string; className?: string }) {
  return <code className={className ? `id ${className}` : 'id'}>{value}</code>;
}

/**
 * One readable string out of a stored title.
 *
 * Titles are `Localised` — `{ en?, es? }` — everywhere in this data, and the console
 * shows whichever exists rather than choosing a language: it is a debugging tool, and
 * the title as stored is the fact being inspected. English first only because that is
 * what the operator reads.
 *
 * The `typeof` branch is not defensiveness about a type. Rendering an object as a
 * React child throws — "objects are not valid as a React child" — and it would take
 * the whole learners table down over one row whose title never got generated, which
 * is exactly the row worth looking at.
 */
export function localTitle(value: unknown, fallback = '(untitled)'): string {
  if (typeof value === 'string') return value || fallback;
  if (value && typeof value === 'object') {
    const copy = value as Record<string, unknown>;
    for (const key of ['en', 'es']) {
      const text = copy[key];
      if (typeof text === 'string' && text) return text;
    }
    // Some other language, which is itself worth seeing: a curriculum that came back
    // in German once, and a title that exists only under `de` is that bug's fingerprint.
    for (const text of Object.values(copy)) {
      if (typeof text === 'string' && text) return text;
    }
  }
  return fallback;
}

/** A missing value, said once, the same way everywhere. An empty cell reads as a bug. */
export function Nil({ what = '—' }: { what?: string }) {
  return <span className="nil">{what}</span>;
}

// ---- Time ----

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "4m ago", "3d ago", "in 2h" — the form the operator actually reasons in.
 *
 * Signed, because half the timestamps in this console are in the future: an item's
 * `due` is the whole point of the FSRS column, and "in 6d" versus "6d ago" is the
 * difference between a schedule working and an overdue backlog.
 */
export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const then = Date.parse(iso);
  // Both ends are checked because `now` is passed in as often as it is defaulted —
  // the send timeline computes "answered after 4m" by handing this the answer's
  // timestamp, and an unparseable one would otherwise render as "NaNd".
  if (Number.isNaN(then) || !Number.isFinite(now)) return iso;
  const delta = now - then;
  const magnitude = Math.abs(delta);
  const unit =
    magnitude < MINUTE
      ? `${Math.round(magnitude / 1000)}s`
      : magnitude < HOUR
        ? `${Math.round(magnitude / MINUTE)}m`
        : magnitude < DAY
          ? `${Math.round(magnitude / HOUR)}h`
          : `${Math.round(magnitude / DAY)}d`;
  return delta >= 0 ? `${unit} ago` : `in ${unit}`;
}

/** True when a due date has passed. Overdue is the state worth colouring. */
export const isOverdue = (iso: string | null | undefined, now = Date.now()): boolean =>
  !!iso && !Number.isNaN(Date.parse(iso)) && Date.parse(iso) < now;

/**
 * The same instant in the learner's own zone.
 *
 * The console's most-asked question is "was that send inside their reach window",
 * and a UTC timestamp cannot answer it — `03:10Z` is either the middle of the night
 * or mid-afternoon depending on a field held somewhere else on the page. So the
 * timeline prints both.
 *
 * A bad zone throws `RangeError` out of `Intl`, which would take the whole timeline
 * with it. A learner row with a mistyped timezone is exactly the sort of thing this
 * console is for looking at, so it degrades to UTC instead of blanking.
 */
export function inZone(iso: string | null | undefined, timezone: string | undefined): string {
  if (!iso) return '—';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(when);
  } catch {
    return `${when.toISOString().slice(0, 16).replace('T', ' ')}Z`;
  }
}

/** A raw timestamp, shortened to the minute. The exact value, for grepping a log. */
export const stamp = (iso: string | null | undefined): string =>
  iso ? `${iso.slice(0, 16).replace('T', ' ')}Z` : '—';

/**
 * Both readings of one instant, and the relative one people read first.
 *
 * `title` carries the full ISO string, because a truncated timestamp is not the one
 * to paste into a CloudWatch time range.
 */
export function When({
  iso,
  timezone,
  now,
}: {
  iso: string | null | undefined;
  timezone?: string;
  now?: number;
}) {
  if (!iso) return <Nil />;
  return (
    <span className="when" title={iso}>
      <span className="when-rel">{ago(iso, now)}</span>
      {timezone && <span className="when-local">{inZone(iso, timezone)}</span>}
      <span className="when-raw">{stamp(iso)}</span>
    </span>
  );
}

// ---- Numbers ----

/** `1.5M`, `23.7k`. Token counters are unreadable at full width in a table. */
export function abbreviate(n: number | undefined): string {
  if (n === undefined || Number.isNaN(n)) return '—';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

export const dollars = (n: number): string => `$${n.toFixed(2)}`;

/**
 * A figure to fixed precision, or a dash.
 *
 * Used everywhere a number arrives from the admin API rather than from this app.
 * `n.toFixed()` on an absent field throws, and one missing `stability` on one item
 * would take the whole 538-item list down with it — a console that white-screens
 * because a field it did not expect was null is worse than no console, because the
 * screen it fails on is the one being used to find out why the field is null.
 */
export const fixed = (n: number | null | undefined, digits = 2): string =>
  typeof n === 'number' && Number.isFinite(n) ? n.toFixed(digits) : '—';

// ---- Failures ----

/**
 * An endpoint that did not answer, said plainly.
 *
 * The 404 case is called out by name because it is the *expected* state until the
 * admin API is deployed: without it every panel on every screen would read
 * "request_failed" and look like a bug in the console instead of a backend that is
 * not there yet.
 */
export function Failed({ error, what }: { error: ApiError; what: string }) {
  return (
    <p className="admin-failed" role="status">
      <strong>{what}</strong> — <code>{error.error}</code>
      {error.reason ? ` (${error.reason})` : ''} · HTTP {error.status}
      {error.status === 404 && ' — this endpoint is not deployed yet'}
      {error.status === 0 && ' — the request never left the browser'}
    </p>
  );
}

export function Waiting({ what }: { what: string }) {
  return <p className="admin-waiting">Loading {what}…</p>;
}

// ---- Paging ----

/** One page from an endpoint, in the shape the paging hook wants it. */
export interface Page<T> {
  rows: T[];
  next?: string;
}

/**
 * How many pages `loadAll` will follow before it stops on its own.
 *
 * A cap rather than a `while (cursor)`: an endpoint that echoes the cursor it was
 * given — a plausible off-by-one in a hand-written pager — would otherwise spin
 * forever, appending the same page and hanging the tab. The repeated-cursor check
 * below catches the common form of that; this catches the rest. 50 pages at 100 rows
 * is 5,000, an order of magnitude past the ~538 items an objective holds.
 */
const MAX_PAGES = 50;

/**
 * A cursor-paginated list, accumulated.
 *
 * Accumulating rather than replacing is the decision that makes 538 items usable:
 * every filter and sort in the item browser runs over everything loaded, so "next
 * page" would mean sorting by lapses within an arbitrary hundred-item slice — a
 * ranking that changes meaning as you page, which is worse than no ranking. So the
 * console pages *forwards only*, and offers to follow the cursor to the end.
 *
 * `fetchPage` must be memoised by the caller: its identity is the reset signal, so
 * changing the node filter starts a new list rather than appending a different
 * objective's items to the old one.
 */
export function usePages<T>(
  fetchPage: (cursor?: string) => Promise<Page<T> | ApiError>,
  /**
   * Whether to follow the cursor to the end without being asked.
   *
   * On for the item browser and off for the message and send logs, and the
   * difference is what each list is for. Judging 538 items means having all 538 —
   * a filter that only sees the first hundred is a filter that lies. A message log
   * is unbounded history where the newest page is almost always the answer, so
   * eagerly pulling every page a learner has ever received would spend requests on
   * rows nobody asked for. Both cases keep the buttons.
   */
  options: { eager?: boolean } = {},
): {
  rows: T[];
  error: ApiError | undefined;
  loading: boolean;
  done: boolean;
  pages: number;
  loadMore: () => void;
  loadAll: () => void;
} {
  const [rows, setRows] = useState<T[]>([]);
  const [error, setError] = useState<ApiError>();
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [pages, setPages] = useState(0);
  const eager = options.eager === true;

  const cursor = useRef<string | undefined>(undefined);
  const seen = useRef(new Set<string>());
  /*
   * Which list these rows belong to. Incremented on every reset, and checked after
   * every await — a slow first page for objective A must not land in objective B's
   * list when the operator clicks through before it arrives. This is the same
   * failure `Research.tsx` guards with its `stale` flag, in a form that survives
   * several requests in flight at once.
   */
  const generation = useRef(0);
  /*
   * Which list has a request running, rather than whether *any* does.
   *
   * A plain boolean here was a real bug: an eager load of one node's items takes
   * several requests, and changing the node filter while it ran incremented the
   * generation and then found the flag still set — so the new list's first fetch
   * returned immediately and the screen sat empty, loading, for ever. Comparing
   * generations instead means a superseded loop can be abandoned mid-flight while the
   * new one starts at once, and only a second click on the *same* list is ignored.
   */
  const inFlight = useRef<number | undefined>(undefined);

  const fetch = useCallback(
    async (all: boolean) => {
      const mine = generation.current;
      if (inFlight.current === mine) return;
      inFlight.current = mine;
      setLoading(true);

      try {
        for (let page = 0; page < MAX_PAGES; page += 1) {
          const result = await fetchPage(cursor.current);
          if (generation.current !== mine) return;

          if (isError(result)) {
            setError(result);
            setDone(true);
            return;
          }

          setError(undefined);
          setRows((current) => [...current, ...result.rows]);
          setPages((n) => n + 1);

          const next = result.next;
          // A cursor we have already followed means the endpoint is not advancing.
          // Stopping and calling the list complete is wrong in a visible way; going
          // round again is wrong in a way that never ends.
          if (!next || seen.current.has(next)) {
            cursor.current = undefined;
            setDone(true);
            return;
          }
          seen.current.add(next);
          cursor.current = next;
          if (!all) return;
        }
        // Hit the ceiling with a cursor still outstanding. Not marked done, so the
        // button stays available and the operator can keep going deliberately.
      } finally {
        if (generation.current === mine) setLoading(false);
        // Only if it is still ours. A superseded loop reaching here must not clear
        // the flag the list that replaced it is holding.
        if (inFlight.current === mine) inFlight.current = undefined;
      }
    },
    [fetchPage],
  );

  useEffect(() => {
    generation.current += 1;
    cursor.current = undefined;
    seen.current = new Set();
    setRows([]);
    setPages(0);
    setDone(false);
    setError(undefined);
    void fetch(eager);
  }, [fetch, eager]);

  return {
    rows,
    error,
    loading,
    done,
    pages,
    loadMore: () => void fetch(false),
    loadAll: () => void fetch(true),
  };
}

// ---- The named writes ----

/**
 * A write, behind a confirmation that states what it costs before it runs (D-19).
 *
 * Two clicks, with the price and the consequence in between, and deliberately not a
 * `window.confirm`: a native dialog cannot show a $2.38 figure as a figure, gets
 * suppressed by browsers that have seen a few of them, and is the one piece of UI
 * that must not be skippable. The cost line is rendered from `PASS_COSTS`, which
 * cites `docs/costs.md` — see the note there.
 *
 * The result is left on screen rather than flashed. A 409 with a reason is the
 * interesting answer here — "already running" is what a second click gets — and a
 * toast that disappears is a reason nobody read.
 */
export function ConfirmedAction({
  label,
  cost,
  consequence,
  basis,
  run,
}: {
  label: string;
  /** Dollars, when the action spends model money. Omitted when it spends none. */
  cost?: number;
  consequence: string;
  basis?: string;
  run: () => Promise<unknown | ApiError>;
}) {
  const [asking, setAsking] = useState(false);
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<{ ok: boolean; text: string } | undefined>();

  return (
    <div className="admin-action">
      <button
        type="button"
        className="admin-button"
        disabled={running}
        onClick={() => {
          setAsking(!asking);
          setOutcome(undefined);
        }}
      >
        {label}
        {cost !== undefined && <span className="admin-price">{dollars(cost)}</span>}
      </button>

      {asking && (
        <div className="admin-confirm" role="group" aria-label={`Confirm: ${label}`}>
          <p className="admin-confirm-cost">
            {cost === undefined ? (
              'No model spend.'
            ) : (
              <>
                This spends about <strong>{dollars(cost)}</strong> of model budget.
              </>
            )}{' '}
            {consequence}
          </p>
          {basis && <p className="admin-confirm-basis">{basis} · figures from docs/costs.md</p>}
          <div className="admin-confirm-row">
            <button
              type="button"
              className="admin-button danger"
              disabled={running}
              onClick={async () => {
                setRunning(true);
                const result = await run();
                setRunning(false);
                setAsking(false);
                if (isError(result)) {
                  setOutcome({
                    ok: false,
                    text: `Refused: ${result.error}${result.reason ? ` (${result.reason})` : ''} · HTTP ${result.status}`,
                  });
                } else {
                  setOutcome({ ok: true, text: 'Accepted. It runs in the background.' });
                }
              }}
            >
              {running ? 'Sending…' : `Yes, ${label.toLowerCase()}`}
            </button>
            <button type="button" className="admin-button quiet" onClick={() => setAsking(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {outcome && (
        <p className={outcome.ok ? 'admin-outcome ok' : 'admin-outcome bad'} role="status">
          {outcome.text}
        </p>
      )}
    </div>
  );
}
